import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CategorySquares } from "@/components/CategorySquares";
import { SizeButtons, BrandButtons } from "@/components/SizeButtons";
import { RecentlySold } from "@/components/RecentlySold";
import { trackPageView } from "@/utils/analytics";
import { captureReferral } from "@/utils/referral";
import { CATEGORY_LABELS, getProductSizes } from "@/utils/whatsapp";
import { Search } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const sortSizes = (a, b) => {
  const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return a.localeCompare(b, undefined, { numeric: true });
};

export default function CategoryPage() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const categoryName = category ? CATEGORY_LABELS[category] || category : "Todos los productos";

  useEffect(() => {
    captureReferral();
    trackPageView(category ? `category-${category}` : "all");
    setSelectedSize("");
    setSelectedBrand("");
    setSearchQuery("");
  }, [category]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch {
        toast.error("No se pudieron cargar los productos");
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const categoryProducts = useMemo(
    () => category
      ? products.filter(p => p.category?.toLowerCase() === category.toLowerCase())
      : products.filter(p => p.category !== "ofertas"),
    [products, category]
  );

  const sizes = useMemo(() => {
    const set = new Set();
    categoryProducts.forEach(p => getProductSizes(p).forEach(s => set.add(s.toUpperCase())));
    return [...set].sort(sortSizes);
  }, [categoryProducts]);

  const brands = useMemo(() => {
    const set = new Set();
    categoryProducts.forEach(p => { if (p.brand?.trim()) set.add(p.brand.trim()); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [categoryProducts]);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return categoryProducts.filter(p =>
      (!selectedSize || getProductSizes(p).some(s => s.toUpperCase() === selectedSize)) &&
      (!selectedBrand || p.brand?.trim() === selectedBrand) &&
      (!q || p.name?.toLowerCase().includes(q) || p.brand?.toLowerCase().includes(q))
    );
  }, [categoryProducts, selectedSize, selectedBrand, searchQuery]);

  const hasFilters = selectedSize || selectedBrand || searchQuery;
  const clearFilters = () => { setSelectedSize(""); setSelectedBrand(""); setSearchQuery(""); };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="category-page">
      <Navbar />

      <section className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <CategorySquares active={category} />
        </div>
      </section>

      {/* Header */}
      <section className="py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <nav className="text-sm text-[#A0A0A0] mb-3">
            <Link to="/" className="hover:text-[#00FF66] transition-colors">Inicio</Link>
            <span className="mx-2 text-white/20">/</span>
            <span className="text-white">{categoryName}</span>
          </nav>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="font-oswald text-3xl sm:text-4xl font-bold text-white uppercase" data-testid="category-title">{categoryName}</h1>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nombre o marca..."
                className="w-full bg-[#121212] border border-white/10 text-white pl-9 pr-3 py-2.5 text-base rounded-full focus:border-[#00FF66] focus:outline-none transition-colors placeholder-[#A0A0A0]/50"
                data-testid="search-input"
              />
            </div>
          </div>
          <p className="text-[#A0A0A0] text-sm mt-2" data-testid="category-count">{filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}</p>
          {(sizes.length > 0 || brands.length > 0) && (
            <div className="mt-5 space-y-3">
              <SizeButtons sizes={sizes} selected={selectedSize} onSelect={setSelectedSize} />
              {brands.length > 1 && <BrandButtons brands={brands} selected={selectedBrand} onSelect={setSelectedBrand} />}
            </div>
          )}
        </div>
      </section>

      {/* Products */}
      <section className="pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#00FF66] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#A0A0A0] mb-4">
                {hasFilters ? "No hay productos con esos filtros" : "No hay productos en esta categoria"}
              </p>
              {hasFilters ? (
                <button onClick={clearFilters} className="inline-block border border-[#00FF66] text-[#00FF66] px-6 py-3 font-semibold rounded-full hover:bg-[#00FF66] hover:text-[#050505] transition-all duration-300" data-testid="clear-size-btn">
                  Quitar filtros
                </button>
              ) : (
                <Link
                  to="/"
                  className="inline-block border border-[#00FF66] text-[#00FF66] px-6 py-3 font-semibold rounded-full hover:bg-[#00FF66] hover:text-[#050505] transition-all duration-300"
                  data-testid="back-to-home-link"
                >
                  Ver todos los productos
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      <RecentlySold category={category} />

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
